const Storage = require('utils/storage');
const PersistenceConfig = require('utils/persistence-config');
const { DATA_FETCHING: Types } = require('action-types');

const internals = {
    version: 2,
    maxAgeMs: 24 * 60 * 60 * 1000,
    revision: 0
};

module.exports = { save, restore, clear };

async function save({ token, schoolId, user, bootstrap, isCurrent = () => true }) {

    const revision = internals.revision;

    try {
        if (!internals.isComplete({ schoolId, user, bootstrap })) {
            return false;
        }

        const owner = await internals.fingerprint(token);

        if (!owner || revision !== internals.revision || !isCurrent()) {
            return false;
        }

        Storage.set(PersistenceConfig.keys.app.bootstrap, {
            version: internals.version,
            owner,
            schoolId: Number(schoolId),
            savedAt: Date.now(),
            user,
            bootstrap
        });
        return true;
    }
    catch (error) {
        // Storage is an optimization; unavailable/quota-limited storage must not block login.
        return false;
    }
}

async function restore({ context, token, schoolId }) {

    const { redux: { hooks: redux }, selectors: { all: selectors } } = context;
    const initialState = redux.getState();

    if (!selectors.getIsAuthenticated(initialState) || !selectors.getVerifiedStatus(initialState)
        || selectors.hasCurrentUser?.(initialState)) {
        return false;
    }

    const activeToken = selectors.getApiToken(initialState);
    const revision = internals.revision;

    try {
        const snapshot = Storage.get(PersistenceConfig.keys.app.bootstrap);

        if (!snapshot || snapshot.version !== internals.version
            || snapshot.schoolId !== Number(schoolId)
            || !Number.isFinite(snapshot.savedAt)
            || Date.now() - snapshot.savedAt < 0
            || Date.now() - snapshot.savedAt > internals.maxAgeMs
            || !internals.isComplete(snapshot)) {
            return false;
        }

        const owner = await internals.fingerprint(token);
        const state = redux.getState();

        if (!owner || owner !== snapshot.owner || revision !== internals.revision
            || !selectors.getIsAuthenticated(state) || !selectors.getVerifiedStatus(state)
            || selectors.hasCurrentUser?.(state)
            || selectors.getApiToken(state) !== activeToken) {
            return false;
        }

        redux.dispatch({ type: Types.FETCH_CURRENT_USER.SUCCESS, payload: { result: snapshot.user } });
        redux.dispatch({ type: Types.FETCH_APP_ENTITIES.SUCCESS, payload: { result: snapshot.bootstrap } });
        return true;
    }
    catch (error) {
        return false;
    }
}

function clear() {

    internals.revision += 1;

    try {
        Storage.remove(PersistenceConfig.keys.app.bootstrap);
    }
    catch (error) {
        // Session cleanup must still succeed when storage is unavailable.
    }
}

internals.isComplete = ({ schoolId, user, bootstrap }) => {

    const id = user?.result?.currentUserDetails?.id;
    const school = Number(schoolId);

    return !!id && Number.isSafeInteger(school) && school > 0
        && Number(user?.entities?.users?.[id]?.schoolId) === school
        && !!user?.entities?.userExtensions?.[id]
        && !!bootstrap?.entities?.schools?.[school]
        && !!bootstrap?.entities?.schoolExtensions?.[school]
        && Array.isArray(bootstrap?.result?.roles)
        && Array.isArray(bootstrap?.result?.classes);
};

internals.fingerprint = async (token) => {

    if (!token || !globalThis.crypto?.subtle) {
        return null;
    }

    const digest = await globalThis.crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
};
